/**
 * Lawn codes (lm-ocm.7): a lawn from the builder packed into a short text code, to share as a link
 * (https://playlawnmower.com/?lawn=CODE) or pasted by hand. No server: the code is the lawn.
 *
 * Bytes, then base64url (no padding):
 *
 *   0      version (1)
 *   1, 2   width, height
 *   3      start heading (high nibble, N E S W) and yard (low nibble, YARD_CODES)
 *   4, 5   the name: its two words (indexes into NAME_FIRST and NAME_SECOND)
 *   6, 7   the start (x, y)
 *   ...    the tiles, row by row, run-length: (tile char, run length 1-255) pairs
 *   ...    how many pickups and critters, then (kind code, x, y) each (ENTITY_CODES)
 *   last   a checksum (the sum of the bytes before it, mod 256)
 *
 * The code tables only ever grow at the end, so old codes keep their meaning. A decoded code is
 * never trusted: every value is checked here, and the lawn is checked by the solver before play.
 * The name can only come from the word lists, so a shared lawn is always family-friendly.
 */
import type { BiomeId } from '../content/biomes';
import { TILE_BY_ID, TILES } from '../content/tiles';
import { isSolid } from '../sim/grid';
import {
  BUILDER_CRITTERS,
  BUILDER_PICKUPS,
  BUILDER_TILES,
  LAWN_SIZE,
  NAME_FIRST,
  NAME_SECOND,
  lawnSize,
  startOf,
  tileAt,
  type CustomLawn,
} from './customLawns';

const VERSION = 1;
const DIRS = ['N', 'E', 'S', 'W'] as const;
/** Yards by code (append only). */
const YARD_CODES: readonly BiomeId[] = [
  'backyard',
  'park',
  'farm',
  'golf',
  'cemetery',
  'estate',
  'stadium',
  'meadow',
];
/** Pickups and critters by code (append only). */
const ENTITY_CODES = [
  'coin',
  'gas',
  'repair',
  'squirrel',
  'rabbit',
  'dog',
  'deer',
  'fox',
  'stag',
] as const;

/** The site a shared lawn's link opens. */
export const SHARE_ORIGIN = 'https://playlawnmower.com';

// ── base64url (no browser globals: this module is pure) ──────────────────────────────────────

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64url(bytes: readonly number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const [a, b, c] = [bytes[i]!, bytes[i + 1], bytes[i + 2]];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!;
    if (b !== undefined) out += B64[(n >> 6) & 63]!;
    if (c !== undefined) out += B64[n & 63]!;
  }
  return out;
}

export function fromBase64url(text: string): number[] | null {
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of text) {
    const v = B64.indexOf(ch);
    if (v < 0) return null;
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((value >> bits) & 255);
    }
  }
  return bytes;
}

// ── Encoding ─────────────────────────────────────────────────────────────────────────────────

/** A lawn's code. */
export function encodeLawn(l: Readonly<CustomLawn>): string {
  const { width, height } = lawnSize(l);
  const start = startOf(l);
  const [first = '', second = ''] = l.name.split(' ');
  const bytes: number[] = [
    VERSION,
    width,
    height,
    (DIRS.indexOf(l.startDir) << 4) | Math.max(0, YARD_CODES.indexOf(l.biome)),
    Math.max(0, NAME_FIRST.indexOf(first as (typeof NAME_FIRST)[number])),
    Math.max(0, NAME_SECOND.indexOf(second as (typeof NAME_SECOND)[number])),
    start.x,
    start.y,
  ];
  // The tiles, run-length.
  let run: { ch: number; n: number } | null = null;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const ch = TILE_BY_ID[tileAt(l, x, y)].char.charCodeAt(0);
      if (run && run.ch === ch && run.n < 255) run.n++;
      else {
        if (run) bytes.push(run.ch, run.n);
        run = { ch, n: 1 };
      }
    }
  }
  if (run) bytes.push(run.ch, run.n);
  const entities: { code: string; x: number; y: number }[] = [
    ...l.pickups.map((p) => ({ code: p.kind, x: p.x, y: p.y })),
    ...l.critters.map((c) => ({ code: c.kind, x: c.x, y: c.y })),
  ];
  bytes.push(entities.length);
  for (const e of entities)
    bytes.push((ENTITY_CODES as readonly string[]).indexOf(e.code), e.x, e.y);
  bytes.push(bytes.reduce((a, b) => a + b, 0) & 255);
  return toBase64url(bytes);
}

/** A lawn's link. */
export const lawnLink = (l: Readonly<CustomLawn>): string =>
  `${SHARE_ORIGIN}/?lawn=${encodeLawn(l)}`;

// ── Decoding ─────────────────────────────────────────────────────────────────────────────────

/**
 * The code in some pasted text: a link's `lawn=` value, or else the longest run of code
 * characters (a code pasted alone, or inside a message).
 */
export function findLawnCode(text: string): string | null {
  const param = /[?&]lawn=([A-Za-z0-9_-]+)/.exec(text);
  if (param) return param[1]!;
  const runs = text.match(/[A-Za-z0-9_-]{12,}/g) ?? [];
  return runs.sort((a, b) => b.length - a.length)[0] ?? null;
}

export type DecodeResult = { ok: true; lawn: CustomLawn } | { ok: false; problem: string };

const BAD: DecodeResult = { ok: false, problem: "That code doesn't look right." };

/** A lawn from its code (with id `my-0`: the caller gives it a real one), or what is wrong. */
export function decodeLawn(code: string): DecodeResult {
  const bytes = fromBase64url(code.trim());
  if (!bytes || bytes.length < 10) return BAD;
  const sum = bytes.slice(0, -1).reduce((a, b) => a + b, 0) & 255;
  if (sum !== bytes[bytes.length - 1]) return BAD;
  if (bytes[0] !== VERSION) {
    return { ok: false, problem: 'That lawn needs a newer version of the game.' };
  }
  const [, width = 0, height = 0, look = 0, nameA = 0, nameB = 0, sx = 0, sy = 0] = bytes;
  if (width < LAWN_SIZE.minW || width > LAWN_SIZE.maxW) return BAD;
  if (height < LAWN_SIZE.minH || height > LAWN_SIZE.maxH) return BAD;
  const dir = DIRS[look >> 4];
  const biome = YARD_CODES[look & 15];
  const first = NAME_FIRST[nameA];
  const second = NAME_SECOND[nameB];
  if (!dir || !biome || !first || !second || sx >= width || sy >= height) return BAD;
  // The tiles: runs of buildable tile chars that fill the lawn exactly.
  const buildable = new Set(BUILDER_TILES.map((id) => TILE_BY_ID[id].char));
  let i = 8;
  let cells = '';
  while (cells.length < width * height) {
    const ch = bytes[i];
    const n = bytes[i + 1];
    if (ch === undefined || !n) return BAD;
    const c = String.fromCharCode(ch);
    if (!buildable.has(c)) return BAD;
    cells += c.repeat(n);
    i += 2;
  }
  if (cells.length !== width * height) return BAD;
  const solidAt = (x: number, y: number) => {
    const def = TILES.find((t) => t.char === cells[y * width + x]);
    return !def || isSolid(def.kind);
  };
  if (solidAt(sx, sy)) return BAD;
  const rows = Array.from({ length: height }, (_, y) => {
    const row = cells.slice(y * width, (y + 1) * width);
    return y === sy ? row.slice(0, sx) + 'S' + row.slice(sx + 1) : row;
  });
  // Pickups and critters: on open ground, off the start, one to a tile.
  const count = bytes[i++] ?? -1;
  if (count < 0 || i + count * 3 !== bytes.length - 1) return BAD;
  const pickups: CustomLawn['pickups'] = [];
  const critters: CustomLawn['critters'] = [];
  const taken = new Set<string>([`${sx},${sy}`]);
  for (let k = 0; k < count; k++) {
    const kind = ENTITY_CODES[bytes[i]!];
    const x = bytes[i + 1]!;
    const y = bytes[i + 2]!;
    i += 3;
    if (!kind || x >= width || y >= height || solidAt(x, y) || taken.has(`${x},${y}`)) return BAD;
    taken.add(`${x},${y}`);
    if ((BUILDER_PICKUPS as readonly string[]).includes(kind)) {
      pickups.push({ kind: kind as (typeof BUILDER_PICKUPS)[number], x, y });
    } else if ((BUILDER_CRITTERS as readonly string[]).includes(kind)) {
      critters.push({ kind: kind as CustomLawn['critters'][number]['kind'], x, y });
    } else return BAD;
  }
  return {
    ok: true,
    lawn: {
      id: 'my-0',
      name: `${first} ${second}`,
      biome,
      rows,
      startDir: dir,
      pickups,
      critters,
    },
  };
}
